import {
  // search actions
  SET_SEARCH_QUERY,
  SET_DEFAULT_RADIUS,
  RESET_QUERY_PARAM,
  RESET_LOCATION_PARAM,
  SET_SEARCH_RESULTS,
  SET_SEARCH_RESULTS_IS_BOOKED,
  ADD_SEARCH_RESULTS,
  RESET_SEARCH,
  SET_TYPE_SPECIFIC_SEARCH_PARAMS,
  // filter actions
  SET_FILTERS,
  RESET_FILTERS,
  SET_SEARCH_LOCATION_DISTANCE,
} from './types'
import navigatorAPI from 'settings/xhr/navigatorAPI'
import axios from 'axios'
import _ from 'lodash'

// helpers
import { getEntryPoint } from 'settings/app/helpers'
import { buildUrl, getSearchUrl } from 'settings/app/urlHelpers'
import staticLabels from 'settings/app/staticLabels.json'

// static value
import {
  entryPointId,
  PAGE_TYPES,
  EMPTY_SEARCH_DATA,
  ADDITIONAL_SEARCHBAR_SELECTS,
  RESULT_TYPE_SEARCH_PARAMS,
} from 'settings/app/constants'
import { hideAPIErrorInfo, showAPIErrorInfo } from './errorActions'
import { getQsrIdentifierArray } from './compareActions'
import { googleDistanceRequest } from 'settings/xhr/axiosRequests'

/**
 * setter for result tile specific search params
 * @param resultTyp
 * @returns {(function(*): void)|*}
 */
export const setTypeSpecificSearchParams = (resultTyp) => (dispatch) => {
  try {
    const params = {}
    RESULT_TYPE_SEARCH_PARAMS[resultTyp].forEach((key) => {
      params[key] = ''
    })

    if (!!ADDITIONAL_SEARCHBAR_SELECTS[resultTyp]) {
      Object.keys(ADDITIONAL_SEARCHBAR_SELECTS[resultTyp]).forEach((_, k) => {
        params[ADDITIONAL_SEARCHBAR_SELECTS[resultTyp][k].typ] =
          ADDITIONAL_SEARCHBAR_SELECTS[resultTyp][k].preSelect
      })
    }

    dispatch({
      type: SET_TYPE_SPECIFIC_SEARCH_PARAMS,
      payload: params,
    })
  } catch (e) {
    throw new Error(`Error in fn.setTypeSpecificSearchParams: MSG => ${ e }`)
  }
}

/**
 * request google api
 * append location data
 * @param resultIDs
 */
export const getLocationDistanceFromResults =
  (resultIDs) => (dispatch, getState) =>
    new Promise((resolve) => {
      const setLocation = async () => {
        const { results, searchLocation } = getState().search

        const requestResults = {}

        Object.keys(results).forEach(async (item, key) => {
          if (!resultIDs.includes(results[key].id)) return true

          // google api needs lat and lng as property
          requestResults[key] = {
            lat: results[key].coordinates.lat,
            lng: results[key].coordinates.lon,
          }
        })

        const distances = await googleDistanceRequest({
          origin: searchLocation,
          destinations: Object.values(requestResults),
        })

        Object.keys(requestResults).forEach((key, index) => {
          results[key].locationDistance = distances[index]
        })

        dispatch({
          type: SET_SEARCH_LOCATION_DISTANCE,
          payload: results,
        })

        resolve()
      }

      setTimeout(setLocation, 2000)
    })

/**
 * persist query params when search button is pressed and all params are valid
 * @param query
 * @param location
 * @param radius
 * @returns {{payload: {query: *, location: *, radius: *}, type: string}}
 */
export const setSearchQueryParam = ({ query, location, radius }) => ({
  type: SET_SEARCH_QUERY,
  payload: { query, location, radius },
})

/**
 * reset location and radius
 * @returns {{type: string}}
 */
export const deleteLocationQueryParam = (radius) => ({
  type: RESET_LOCATION_PARAM,
  payload: radius,
})

/**
 * reset query param
 * @returns {{type: string}}
 */
export const deleteQueryParam = () => ({
  type: RESET_QUERY_PARAM,
})

/**
 * reset query or location param from store
 * @returns {{payload: number, type: string}}
 * @param radius
 */
export const setDefaultRadius = (radius) => ({
  type: SET_DEFAULT_RADIUS,
  payload: parseInt(radius, 10),
})

/**
 * load more results
 * @param from
 * @param size
 * @returns {(function(*, *): Promise<string|undefined>)|*}
 */
export const getMoreResults =
  ({ from = 0, size = 10 }) =>
  async (dispatch, getState) => {
    if (!!getState().apiError) {
      dispatch(hideAPIErrorInfo())
    }

    const [apiUrl, getUrl] = buildUrl(getState(), { from, size })
    const currentEntryPoint = getState().application.entryPoint

    try {
      let data = null

      await navigatorAPI
        .get(apiUrl)
        .then((response) => {
          // handle success
          data = response.data
        })
        .catch((error) => {
          // handle error
          console.error('fetch more results error: ', error)
          // only show error if request was not canceled
          if (!axios.isCancel(error)) {
            dispatch(showAPIErrorInfo())
          }
        })
      if (data) {
        // get qsr identifier index
        const qsrIdentifier =
          currentEntryPoint === entryPointId.HOSPITALS
            ? getQsrIdentifierArray([
                ...data.results,
                ...Object.values(getState().search.results),
              ])
            : []

        dispatch({
          type: ADD_SEARCH_RESULTS,
          payload: {
            results: data.results,
            qsrIdentifier,
            from: from + size,
            size,
          },
        })
      }
      // callback(getUrl)
      return Promise.resolve(getUrl)
    } catch (e) {
      if (e) {
        if (e.Cancel) {
          // console.log('previous request canceled')
        } else {
          console.error(e)
          return Promise.reject(e)
        }
      }
    }
  }

/**
 * Toggle isBooked value
 * @param {string} index
 */
export const setIsBooked = (index) => ({
  type: SET_SEARCH_RESULTS_IS_BOOKED,
  payload: { index },
})

/**
 * reset query or location param from store
 * @returns {{type: string}}
 */
export const resetSearchResults = () => (dispatch) => {
  dispatch({
    type: RESET_SEARCH,
  })
  dispatch({
    type: RESET_FILTERS,
  })
  dispatch({
    type: RESET_QUERY_PARAM,
  })
}

/**
 *  search Handler
 *
 * @param searchTyp
 * => initialSearch = perform a clean search with initial_search param (ignore filters, sort)
 * => increaseRadius = perform a search with all params from url (get) and increase radius value
 * => deepLinkSearch = perform a search with all params from url (get)
 *
 * @param searchTyp
 * @param params
 * @param callback
 * @returns {(function(*, *): Promise<void>)|*}
 */
export const searchHandler =
  (searchTyp, params, callback = false) =>
  async (dispatch, getState) => {
    if (!!getState().apiError) {
      dispatch(hideAPIErrorInfo())
    }

    /**
     * Workaround MGMNAVSUP-60
     */
    if (searchTyp === 'deepLinkSearch') {
      if (params.query !== '' && !params.type) {
        params.type = getState().search.prevParams.type
      }

      if (params.radius && 'undefined' === typeof params.location) {
        params.radius = 0
      }
    }

    const entryPoint = getEntryPoint()
    const { location = '', radius = '', from = 0, size = 10 } = params

    let getUrl, apiUrl, parsedRadius
    let parsedSize = size && parseInt(size, 10)

    if (searchTyp === 'initialSearch') {
      getUrl = getSearchUrl({ ...params, initial_search: true })
      apiUrl = `${ getState().application.entryPoint }/${ getUrl }`
      parsedRadius = radius
    } else {
      const { clientHasSearched } = getState().search

      // additional Select data no converting
      let noConvertKeys = !!ADDITIONAL_SEARCHBAR_SELECTS[entryPoint]
        ? Object.keys(ADDITIONAL_SEARCHBAR_SELECTS[entryPoint]).map(
            (i, k) => ADDITIONAL_SEARCHBAR_SELECTS[entryPoint][k].typ
          )
        : []

      noConvertKeys = [...noConvertKeys, 'lat', 'lng']

      // convert string into int
      Object.keys(params).forEach((index) => {
        if (noConvertKeys.includes(index)) return true
        if (!isNaN(params[index]) && params[index] !== '') {
          params[index] = parseInt(params[index], 10)
        }
      })

      // deepLink request with radius 'federal wide' has no radius if location is set
      parsedRadius =
        (searchTyp === 'deepLinkSearch') & (params.location !== '') &&
        params.radius === undefined
          ? 0
          : params.radius

      if (parsedSize !== from) parsedSize = parsedSize <= 50 ? parsedSize : 50

      // in initial search and there is no location, radius should be nationwide wide
      if (
        !clientHasSearched &&
        searchTyp === 'deepLinkSearch' &&
        location.trim() === '' &&
        localStorage.getItem('pageType') === PAGE_TYPES.RESULT_PAGE
      ) {
        parsedRadius = 0
      }

      // get url string for api call and history push
      const requestUrls = buildUrl(getState(), { ...params, size: parsedSize })

      apiUrl = requestUrls[0].replace(/\[\]\[\]/g, '[]')
      getUrl = requestUrls[1]
    }

    // check if search context has changed
    const prevParams = { ...getState().search.prevParams }
    const { searchParamsHasChanged, newPrevParams } =
      checkIfSearchContextHasChanged({ prevParams, params })
    newPrevParams.radius = parsedRadius

    // refresh urls string (append filters && sorting)
    if (!searchParamsHasChanged) {
      const { collection } = getState().filter

      if (params.radius === '') {
        params.radius = 0
      }

      const requestUrls = buildUrl(getState(), {
        ...params,
        size: parsedSize,
        collection,
      })
      apiUrl = requestUrls[0].replace(/\[\]\[\]/g, '[]')
      getUrl = requestUrls[1]
    }

    let data = null

    // perform search xhr
    await navigatorAPI
      .get(apiUrl)
      .then((response) => {
        data = response.data
      })
      .catch((error) => {
        if (error.response) {
          if (error.response.status === 500 || error.response.status === 400) {
            if (error.response.status === 500) dispatch(showAPIErrorInfo())
            data = EMPTY_SEARCH_DATA // reset previous results

            if (entryPointId.CROSS === entryPoint) {
              data.searches = []
            }
          }

          // this error course xhr request with nonsense location or invalide get params
          // TODO :: better way to solve this, to get an clean empty response with this code (BE PART)
          if (error.response.hasOwnProperty('data')) {
            const { errors } = error.response.data
            if (
              400 === error.response.status &&
              errors.length > 0 &&
              errors[0].code === 101
            ) {
              data = EMPTY_SEARCH_DATA

              if (entryPointId.CROSS === entryPoint) {
                data.searches = []
              }
            }
          }
        }
      })

    if (!data) return

    /** ********************************************************/
    /*              RESPONSE/DATA MAPPING                     */
    /** *******************************************************/
    let results = {}
    let generalTooltips = {}
    let noResults, count
    let qsrIdentifier = []
    let factsBox = []
    let generalHints = []

    // QSS SEARCH || SPECIFIC SEARCHES (TYP BASED)
    if (entryPointId.CROSS === entryPoint) {
      let numberOfResults = 0

      // correct wrong sorting
      const sortedResults = {}
      Object.keys(data.searches).forEach((resultType) => {
        // sorting found || no sorting (only add accessible typ)
        if (
          data.searches[resultType].hasOwnProperty('position') &&
          data.searches[resultType].position !== null
        ) {
          sortedResults[data.searches[resultType].position] =
            data.searches[resultType]
          sortedResults[data.searches[resultType].position].collapsed =
            !sortedResults[data.searches[resultType].position].collapsed
          sortedResults[data.searches[resultType].position].typ = resultType
        } else {
          data.searches[resultType].typ = resultType
        }
        numberOfResults += data.searches[resultType].count

        // hospital specific data
        if (resultType === 'hospitals') {
          generalTooltips = data.searches[resultType].generalTooltips
          qsrIdentifier = getQsrIdentifierArray(
            data.searches[resultType].results
          )
        }
      })

      results =
        0 < Object.keys(sortedResults).length ? sortedResults : data.searches
      noResults = 0 === numberOfResults
      count = numberOfResults
      factsBox = data.factsBox
    } else {
      noResults = !data.results || data.results.length === 0
      count = data.count
      results = data.results
      generalHints = data.generalHints
      generalTooltips = data.generalTooltips
    }

    // static implementation https://bugs.onedev.aok.de/browse/MGMPMNAV-1535 && remove later if hint for midwives provided at response
    if (
      (searchTyp === 'initialSearch' || searchTyp === 'deepLinkSearch') &&
      entryPoint === entryPointId.MIDWIVES
    ) {
      generalHints.push({
        redirect: null,
        text: staticLabels.searchActionMidwivesHint,
      })
    }

    // set possible qsr values (only hospitals)
    if (entryPoint === entryPointId.HOSPITALS) {
      qsrIdentifier = getQsrIdentifierArray(results)
    }

    // hide duplicate results for caresupport (also happens in filterActions.js)
    // if(entryPoint === entryPointId.CARESUPPORT) {
    //   results = filterDuplicateResults(results)
    //   if(data.results.length - results.length > 0) {
    //     count = count - (data.results.length - results.length)
    //     console.error(`Duplicate results found for caresupport! Duplicates that were removed:`, _.difference(data.results, results))
    //   }
    // }

    // persist results
    if (data) {
      dispatch({
        type: SET_SEARCH_RESULTS,
        payload: {
          results,
          prevParams: newPrevParams,
          params: {
            ...getFilteredSearchParams(entryPoint, params),
            radius: parsedRadius,
          },
          count,
          noResults,
          isTreatmentReason: data.is_treatment_reason,
          isAop: data.is_aop,
          location: data.location,
          from: parseInt(from, 10) + parsedSize,
          generalTooltips,
          factsBox,
          generalHints,
          showDetails: data.show_details,
          qsrIdentifier,
          services: data.aokServices,
          suggestions: data.suggestions,
          searchLocation:
            !!params.lat && !!params.lng
              ? { lat: params.lat, lng: params.lng }
              : { lat: false, lng: false },
        },
      })

      // persist filters && sorting option
      if (entryPoint !== entryPointId.CROSS) {
        dispatch({
          type: SET_FILTERS,
          payload: {
            // tmp fix (careservices should not have filters)
            collection:
              entryPoint !== entryPointId.CARESERVICES
                ? data.filters.filter((el) => el.key !== 'carehomes_care_typ')
                : [],
            sorting: data.sorting,
            activeSort: data.active_sorting,
          },
        })
      }

      if (searchTyp === 'initialSearch' || searchTyp === 'increaseRadius') {
        callback(getUrl)
      }
    }
  }

/**
 * filter allowed search params
 * considering additional select props
 * @param entryPoint
 * @param params
 * @returns {{}}
 */
const getFilteredSearchParams = (entryPoint, params = {}) => {
  // let SEARCH_PARAMS_ALLOWED_KEYS = ['key', 'location', 'query', 'type']
  let SEARCH_PARAMS_ALLOWED_KEYS = RESULT_TYPE_SEARCH_PARAMS[entryPoint]

  // merge additional params
  if (ADDITIONAL_SEARCHBAR_SELECTS[entryPoint]) {
    SEARCH_PARAMS_ALLOWED_KEYS = [
      ...SEARCH_PARAMS_ALLOWED_KEYS,
      ...ADDITIONAL_SEARCHBAR_SELECTS[entryPoint].map((k) => k.typ),
    ]
  }

  const filteredParams = {}
  SEARCH_PARAMS_ALLOWED_KEYS.forEach((key) => {
    if (params[key]) {
      filteredParams[key] = params[key]
      return true
    }
    filteredParams[key] = ''
  })
  return filteredParams
}

/**
 * check if search context has changed
 * YES => client force new search
 * NO => client search with previous filter set
 * @param prevParams
 * @param params
 * @returns {{searchParamsHasChanged: boolean, newPrevParams: {query: string, location, type, key}}}
 */
const checkIfSearchContextHasChanged = ({ prevParams, params }) => {
  const entryPoint = getEntryPoint()
  const { query = '', key, type, location } = params
  let searchParamsHasChanged
  let newPrevParams = {}

  switch (entryPoint) {
    case entryPointId.CROSS:
    case entryPointId.HOSPITALS:
    case entryPointId.DOCTORS:
    case entryPointId.MIDWIVES:
      searchParamsHasChanged =
        JSON.stringify({
          query: prevParams.query,
          key: prevParams.key,
          type: prevParams.type,
        }) !== JSON.stringify({ query, key, type })

      newPrevParams = { query, key, type, location }
      break

    default:
      searchParamsHasChanged =
        JSON.stringify({
          query: prevParams.query,
        }) !== JSON.stringify({ query })

      newPrevParams = { query, location }
  }

  if (!!ADDITIONAL_SEARCHBAR_SELECTS[entryPoint]) {
    Object.keys(ADDITIONAL_SEARCHBAR_SELECTS[entryPoint]).forEach((_, k) => {
      newPrevParams[ADDITIONAL_SEARCHBAR_SELECTS[entryPoint][k].typ] =
        params[ADDITIONAL_SEARCHBAR_SELECTS[entryPoint][k].typ]
    })
  }

  return {
    searchParamsHasChanged,
    newPrevParams,
  }
}
